import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { mp } from './analytics'; // your util/mixpanel.ts
import {
  ROUTE_ACCOUNT_ACCOUNT_PASSWORD,
  ROUTE_ACCOUNT_APPOINTMENTS,
  ROUTE_ACCOUNT_CARS,
  ROUTE_ACCOUNT_CUSTOMER_INFORMATION,
  ROUTE_ACCOUNT_APPOINTMENTS_DETAIL,
  ROUTE_FRANCHISE_MANAGER_CREATE_CUSTOMER,
  ROUTE_ACCOUNT_ROOT,
  ROUTE_AUTH_FORGOT_PASSWORD,
  ROUTE_AUTH_LOGIN,
  ROUTE_BOOK_ADD_ONS,
  ROUTE_BOOK_CONFIRMATION,
  ROUTE_BOOK_LOCATION,
  ROUTE_BOOK_ROOT,
  ROUTE_BOOK_SCHEDULE,
  ROUTE_BOOK_SERVICES,
  ROUTE_FRANCHISE_MANAGER,
  ROUTE_FRANCHISE_MANAGER_WORK_ORDERS,
  ROUTE_ROOT,
  ROUTE_FRANCHISE_MANAGER_WORK_ORDER_DETAIL,
  ROUTE_FRANCHISE_MANAGER_INVOICE_DETAIL,
  ROUTE_FRANCHISE_MANAGER_CUSTOMERS,
  ROUTE_FRANCHISE_MANAGER_CUSTOMER_DETAIL,
  ROUTE_FRANCHISE_MANAGER_PAYOUTS,
  ROUTE_FRANCHISE_TECHNICIAN_PAYOUTS,
  ROUTE_FRANCHISE_SALES_PAYOUTS,
  ROUTE_FRANCHISE_MANAGER_PAYOUT_DETAIL,
  ROUTE_FRANCHISE_MANAGER_ANALYTICS,
  ROUTE_FRANCHISE_SALES_ANALYTICS,
  ROUTE_FRANCHISE_INVENTORY,
  ROUTE_FRANCHISE_MANAGER_VANS_PAYOUTS,
  ROUTE_AUTH_SIGN_UP,
} from './constants';

const routeNames: Record<string, string> = {
  [ROUTE_ROOT]: 'Root Redirect',
  [ROUTE_AUTH_LOGIN]: 'Login Page Viewed',
  [ROUTE_AUTH_SIGN_UP]: 'Signup Page Viewed',
  [ROUTE_AUTH_FORGOT_PASSWORD]: 'Forgot Password Page Viewed',

  [ROUTE_ACCOUNT_ROOT]: 'Account Home Page Viewed',
  [ROUTE_ACCOUNT_APPOINTMENTS]: 'Customer - Appointments Page Viewed',
  [ROUTE_ACCOUNT_APPOINTMENTS_DETAIL]:
  'Customer - Appointment Details Page Viewed',
  [ROUTE_ACCOUNT_CARS]: 'Customer - My Cars Page Viewed',
  [ROUTE_ACCOUNT_CUSTOMER_INFORMATION]: 'Customer - Information Page Viewed',
  [ROUTE_ACCOUNT_ACCOUNT_PASSWORD]: 'Customer - Account Password Page Viewed',

  [ROUTE_BOOK_ROOT]: 'Booking Root Page Viewed',
  [ROUTE_BOOK_LOCATION]: 'Booking Location Page Viewed',
  [ROUTE_BOOK_SERVICES]: 'Booking Services Page Viewed',
  [ROUTE_BOOK_ADD_ONS]: 'Booking Add-ons Page Viewed',
  [ROUTE_BOOK_SCHEDULE]: 'Booking Schedule Page Viewed',
  [ROUTE_BOOK_CONFIRMATION]: 'Booking Confirmation Page Viewed',

  [ROUTE_FRANCHISE_MANAGER]: 'Franchise Manager Dashboard Viewed',
  [ROUTE_FRANCHISE_MANAGER_CREATE_CUSTOMER]:
  'Manager - Create Customer Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_WORK_ORDERS]: 'Manager - Work Orders Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_WORK_ORDER_DETAIL]:
  'Manager - Work Order Detail Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_INVOICE_DETAIL]:
  'Manager -Invoice Detail Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_CUSTOMERS]: 'Manager - Customers Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_CUSTOMER_DETAIL]:
  'Manager - Customer Detail Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_PAYOUTS]: 'Manager - Payout Reports Page Viewed',
  [ROUTE_FRANCHISE_TECHNICIAN_PAYOUTS]: 'Technician Payout Reports Page Viewed',
  [ROUTE_FRANCHISE_SALES_PAYOUTS]: 'Sales Payout Reports Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_VANS_PAYOUTS]:
  'Manager - Vans Payout Reports Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_PAYOUT_DETAIL]:
  'Manager - Payout Detail Page Viewed',
  [ROUTE_FRANCHISE_MANAGER_ANALYTICS]:
  'Franchise Manager Analytics Page Viewed',
  [ROUTE_FRANCHISE_SALES_ANALYTICS]: 'Sales Analytics Page Viewed',
  [ROUTE_FRANCHISE_INVENTORY]: 'Manager/Technitian - Inventory Page Viewed',
};

export default function RouteChangeTracker() {
  const location = useLocation();

  useEffect(() => {
    const name = routeNames[location.pathname] || 'Page Viewed';

    // Start high-precision timer
    const startTime = performance.now();

    // This runs when EVERYTHING is truly loaded (images, fonts, API calls)
    const onFullyLoaded = () => {
      const loadTimeMs = Math.round(performance.now() - startTime);

      mp.track(name, {
        path: location.pathname,
        page_load_ms: loadTimeMs,
        page_load_seconds: Number((loadTimeMs / 1000).toFixed(2)),
      });

      // Keep session tracking
      mp.time_event('Session End');
    };

    // Fire when browser says "fully loaded"
    if (document.readyState === 'complete') {
      onFullyLoaded();
    } else {
      window.addEventListener('load', onFullyLoaded, { once: true });
    }

    // Fallback: if stuck loading >10s, still track what we have
    const fallbackTimer = setTimeout(onFullyLoaded, 10000);

    return () => {
      clearTimeout(fallbackTimer);
      window.removeEventListener('load', onFullyLoaded);
    };
  }, [location.pathname]);

  return null;
}
